/** @jsx jsx */
import React, { type ReactNode } from 'react';
import { jsx, css } from '@compiled/react';
import { ButtonGroup } from '@atlaskit/button';
import { Box, media, xcss } from '@atlaskit/primitives';
import { token } from '@atlaskit/tokens';
import { componentWithCondition } from '@atlassian/jira-feature-flagging-utils';
import Graph from '@atlassian/jira-graph/src/ui/index.tsx';
import { PageTopBar } from '@atlassian/jira-horizontal-nav-page-topbar/src/ui/index.tsx';
import { getWillShowNav4 } from '@atlassian/jira-navigation-apps-sidebar-nav4-rollout-core/src/common/utils/get-will-show-nav4/index.tsx';
// eslint-disable-next-line jira/restricted/@atlassian/jira-platform-ari
import { createAri } from '@atlassian/jira-platform-ari';
import { toProjectKey } from '@atlassian/jira-shared-types/src/general.tsx';
import FeedbackButton from '@atlassian/jira-software-deployments-feedback-button/src/ui/index.tsx';
import HeaderMeatballMenu from '@atlassian/jira-software-deployments-header-meatball-menu/src/ui/index.tsx';
import TitleBreadcrumbs from '@atlassian/jira-software-deployments-title-breadcrumbs/src/ui/index.tsx';
import { useCloudId } from '@atlassian/jira-tenant-context-controller/src/components/cloud-id/index.tsx';
import { NonCriticalErrorBoundary } from '../../common/utils/non-critical-error-boundary';
import { useProjectContextServiceQueryResult } from '../../services/get-project';
import ExportButton from './export-button';

type Props = {
	leftContent: ReactNode;
	rightContent: ReactNode;
};

const HeaderNew = ({ leftContent, rightContent }: Props) => {
	const { data } = useProjectContextServiceQueryResult();
	const cloudId = useCloudId();
	const buttonGroup = (
		<>
			<Graph
				from={createAri({
					resourceId: data?.projectId.toString() ?? '',
					resourceType: 'project',
					resourceOwner: 'jira',
					cloudId,
				})}
			/>
			{rightContent}
			<Box xcss={buttonStyles}>
				<NonCriticalErrorBoundary fallback="flag" eventLocation="exportButton">
					<ExportButton isIconOnly />
				</NonCriticalErrorBoundary>
			</Box>
			<Box xcss={buttonStyles}>
				<HeaderMeatballMenu
					isProjectAdmin={data?.isProjectAdmin ?? false}
					isSimplified={data?.simplified ?? false}
					projectKey={toProjectKey(data?.projectKey ?? '')}
					hasDeployments
				/>
			</Box>
		</>
	);

	return (
		<PageTopBar
			bottomBar={leftContent}
			actions={<Box xcss={actionBarStyles}>{buttonGroup}</Box>}
			wrapAtMd
		/>
	);
};

const HeaderOld = ({ leftContent, rightContent }: Props) => {
	const { data } = useProjectContextServiceQueryResult();
	const cloudId = useCloudId();

	return (
		<>
			<TitleBreadcrumbs
				actions={
					<ButtonGroup>
						<Graph
							from={createAri({
								resourceId: data?.projectId.toString() ?? '',
								resourceType: 'project',
								resourceOwner: 'jira',
								cloudId,
							})}
						/>
						<NonCriticalErrorBoundary fallback="unmount" eventLocation="feedbackButton">
							<FeedbackButton />
						</NonCriticalErrorBoundary>
						<NonCriticalErrorBoundary fallback="flag" eventLocation="exportButton">
							<ExportButton />
						</NonCriticalErrorBoundary>
						{data && (
							<HeaderMeatballMenu
								isProjectAdmin={data.isProjectAdmin ?? false}
								isSimplified={data.simplified}
								projectKey={toProjectKey(data.projectKey)}
								hasDeployments
							/>
						)}
					</ButtonGroup>
				}
			/>
			<div css={filtersRowContainerStyles}>
				{leftContent}
				<Box xcss={rightContentWrapperStyles}>{rightContent}</Box>
			</div>
		</>
	);
};

const filtersRowContainerStyles = css({
	display: 'flex',
	justifyContent: 'space-between',
	alignItems: 'center',
	margin: `${token('space.200', '16px')} 0`,
	flexWrap: 'wrap-reverse',
	[media.above.md]: {
		flexWrap: 'nowrap',
	},
});

const buttonStyles = xcss({
	display: 'inline-block',
	marginLeft: 'space.050',
});

const actionBarStyles = xcss({
	// enough width to fit all action buttons without shrinking
	minWidth: '275px',
	display: 'flex',
	flexShrink: 0,
	justifyContent: 'flex-end',
});

const rightContentWrapperStyles = xcss({
	justifyContent: 'flex-end',
	display: 'flex',
	flex: '1 1 auto',
	[media.above.md]: {
		flex: '0 1 auto',
	},
});

export default componentWithCondition(() => getWillShowNav4(), HeaderNew, HeaderOld);
